import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Save, Globe } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import MediaUploader from '../../components/MediaUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SEOManager = () => {
  const [pages, setPages] = useState([
    { key: 'home', label: 'Home Page' },
    { key: 'services', label: 'Services Page' },
    { key: 'about', label: 'About Page' },
    { key: 'contact', label: 'Contact Page' }
  ]);
  const [selectedPage, setSelectedPage] = useState('home');
  const [seoData, setSeoData] = useState({
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_title: '',
    og_description: '',
    og_image: '',
    og_type: 'website',
    twitter_card: 'summary_large_image'
  });
  const [gaSettings, setGaSettings] = useState({
    google_analytics_id: '',
    enabled: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSEOData(selectedPage);
    fetchGASettings();
  }, [selectedPage]);

  const fetchSEOData = async (page) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/seo/${page}`);
      setSeoData({
        meta_title: response.data.meta_title || '',
        meta_description: response.data.meta_description || '',
        meta_keywords: response.data.meta_keywords || '',
        og_title: response.data.og_title || '',
        og_description: response.data.og_description || '',
        og_image: response.data.og_image || '',
        og_type: response.data.og_type || 'website',
        twitter_card: response.data.twitter_card || 'summary_large_image'
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching SEO data:', error);
      setLoading(false);
    }
  };

  const fetchGASettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/analytics/settings`);
      setGaSettings(response.data);
    } catch (error) {
      console.error('Error fetching GA settings:', error);
    }
  };

  const handleSaveSEO = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/cms/seo/${selectedPage}`, seoData);
      toast({
        title: "Success",
        description: "SEO data updated successfully"
      });
    } catch (error) {
      console.error('Error updating SEO data:', error);
      toast({
        title: "Error",
        description: "Failed to update SEO data",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGA = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/cms/analytics/settings`, null, {
        params: { google_analytics_id: gaSettings.google_analytics_id }
      });
      toast({
        title: "Success",
        description: "Google Analytics settings updated"
      });
      fetchGASettings();
    } catch (error) {
      console.error('Error updating GA settings:', error);
      toast({
        title: "Error",
        description: "Failed to update GA settings",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading SEO settings...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-light text-[#2d2d2d]">SEO Management</h1>
        <Button
          onClick={handleSaveSEO}
          disabled={saving}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save SEO Settings'}
        </Button>
      </div>

      {/* Page Selector */}
      <div className="bg-white p-4 rounded-lg shadow-lg mb-6">
        <label className="block text-sm font-normal text-[#2d2d2d] mb-3">Select Page</label>
        <div className="flex gap-2 flex-wrap">
          {pages.map((page) => (
            <Button
              key={page.key}
              onClick={() => setSelectedPage(page.key)}
              variant={selectedPage === page.key ? 'default' : 'outline'}
              className={selectedPage === page.key ? 'bg-[#0A1A50]' : ''}
            >
              <Globe className="w-4 h-4 mr-2" />
              {page.label}
            </Button>
          ))}
        </div>
      </div>

      {/* SEO Form */}
      <div className="bg-white p-6 rounded-lg shadow-lg space-y-6 mb-6">
        <h2 className="text-2xl font-light text-[#2d2d2d]">
          SEO for {pages.find(p => p.key === selectedPage)?.label}
        </h2>

        {/* Basic Meta Tags */}
        <div className="space-y-4">
          <h3 className="text-lg font-normal text-[#2d2d2d]">Basic Meta Tags</h3>
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Meta Title</label>
            <Input
              value={seoData.meta_title}
              onChange={(e) => setSeoData({ ...seoData, meta_title: e.target.value })}
              placeholder="Reagle - Premium Real Estate Services"
              maxLength={60}
            />
            <p className="text-xs text-[#2d2d2d] mt-1">{seoData.meta_title.length}/60 characters</p>
          </div>

          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Meta Description</label>
            <Textarea
              value={seoData.meta_description}
              onChange={(e) => setSeoData({ ...seoData, meta_description: e.target.value })}
              placeholder="Expert advisory, development, and investment services for premium real estate projects."
              rows={3}
              maxLength={160}
            />
            <p className="text-xs text-[#2d2d2d] mt-1">{seoData.meta_description.length}/160 characters</p>
          </div>

          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Meta Keywords</label>
            <Input
              value={seoData.meta_keywords}
              onChange={(e) => setSeoData({ ...seoData, meta_keywords: e.target.value })}
              placeholder="real estate, advisory, development, investments"
            />
          </div>
        </div>

        {/* Open Graph Tags */}
        <div className="space-y-4 pt-4 border-t">
          <h3 className="text-lg font-normal text-[#2d2d2d]">Social Media (Open Graph)</h3>
          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">OG Title</label>
            <Input
              value={seoData.og_title}
              onChange={(e) => setSeoData({ ...seoData, og_title: e.target.value })}
              placeholder="Leave empty to use Meta Title"
            />
          </div>

          <div>
            <label className="block text-sm font-normal text-[#2d2d2d] mb-2">OG Description</label>
            <Textarea
              value={seoData.og_description}
              onChange={(e) => setSeoData({ ...seoData, og_description: e.target.value })}
              placeholder="Leave empty to use Meta Description"
              rows={2}
            />
          </div>

          <MediaUploader
            label="OG Image (Social Sharing)"
            currentUrl={seoData.og_image}
            onMediaSelect={(url) => setSeoData({ ...seoData, og_image: url })}
            accept="image/*"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-normal text-[#2d2d2d] mb-2">OG Type</label>
              <Input
                value={seoData.og_type}
                onChange={(e) => setSeoData({ ...seoData, og_type: e.target.value })}
                placeholder="website"
              />
            </div>
            <div>
              <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Twitter Card</label>
              <select
                value={seoData.twitter_card}
                onChange={(e) => setSeoData({ ...seoData, twitter_card: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="summary">Summary</option>
                <option value="summary_large_image">Summary with Large Image</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Google Analytics */}
      <div className="bg-white p-6 rounded-lg shadow-lg space-y-4">
        <h2 className="text-2xl font-light text-[#2d2d2d]">Google Analytics</h2>
        <div>
          <label className="block text-sm font-normal text-[#2d2d2d] mb-2">
            Google Analytics Measurement ID
          </label>
          <div className="flex gap-2">
            <Input
              value={gaSettings.google_analytics_id}
              onChange={(e) => setGaSettings({ ...gaSettings, google_analytics_id: e.target.value })}
              placeholder="G-XXXXXXXXXX or UA-XXXXXXXXX-X"
              className="flex-1"
            />
            <Button
              onClick={handleSaveGA}
              className="bg-[#0A1A50] hover:bg-[#060F2E] text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </div>
          <p className="text-xs text-[#2d2d2d] mt-2">
            Status: {gaSettings.enabled ? '✅ Enabled' : '⚠️ Not configured'}
          </p>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-blue-800">
            💡 <strong>How to get GA Measurement ID:</strong><br />
            1. Go to <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Analytics</a><br />
            2. Create a property or use existing one<br />
            3. Copy the Measurement ID (starts with G- or UA-)
          </p>
        </div>
      </div>
    </div>
  );
};

export default SEOManager;
